import React from 'react';
import './MonitoringScheduleSelect.scss';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';
import { MonitoringSchedulePreset } from '../../../models/projects/MonitoringInterval';
import CustomTooltip from '../tooltip/CustomTooltip';
import DropdownItem from '../../dropdown-item/DropdownItem';

interface MonitoringScheduleSelectProps {
  value: MonitoringSchedulePreset;
  onChange: (preset: MonitoringSchedulePreset) => void;
  /**
   * Markup variant. `wizard` emits a field block shaped exactly like the wizard's country block and sits
   * right under it (so the wizard's own form rules style it: full-width dropdown, 12px label with the
   * info icon in line, the same bottom spacing); `dialog` emits the edit dialog's label + control pair.
   * No styling of its own beyond dialog spacing.
   */
  variant?: 'dialog' | 'wizard';
}

/**
 * The monitoring-frequency control shared by the "Edit project" dialog and the wizard's last step.
 * Callers gate it with `useModuleAccess('MANAGE_MONITORING_SCHEDULE', …, { allowSuperAdmin: true })`:
 * the backend always sends the schedule, only the editing (and this view) is behind the addon.
 * Deliberately no measurement dates here (decision 2026-10-08): "next measurement" is the start of the
 * next window, which can differ from the day questions are actually sent, so it misled more than it told.
 * The explanatory hint is an info-icon tooltip in the wizard only (same pattern as the country selector's
 * market hint); the edit dialog shows the bare label.
 */
const MonitoringScheduleSelect: React.FC<MonitoringScheduleSelectProps> = ({ value, onChange, variant = 'dialog' }) => {
  const { t } = useTranslation();
  const options: { label: string; value: MonitoringSchedulePreset }[] = [
    { label: t('monitoringScheduleDaily'), value: 'DAILY' },
    { label: t('monitoringScheduleWeekly'), value: 'WEEKLY' },
  ];

  // Same inner markup and size modifier as the country selector next to it: `DropdownItem` for the
  // rows and the selected value, `dropdown--xxl` on the wizard forms (46px, like `input--xxl` / `btn--xxl`).
  const dropdown = (
    <Dropdown
      value={value}
      options={options}
      optionLabel="label"
      onChange={(e) => onChange(e.value as MonitoringSchedulePreset)}
      className={variant === 'wizard' ? 'dropdown dropdown--xxl' : 'dropdown'}
      itemTemplate={(option) => <DropdownItem name={option.label} />}
      valueTemplate={(option) => <DropdownItem name={option?.label ?? ''} />}
    />
  );

  if (variant === 'wizard') {
    return (
      <div className="form-box__field">
        <div className="form__label">
          {t('monitoringSchedule')}:
          <CustomTooltip tooltipText="monitoringScheduleHint" />
        </div>
        {dropdown}
      </div>
    );
  }

  return (
    <div className="monitoring-schedule-select">
      <div className="form-box__label">{t('monitoringSchedule')}:</div>
      {dropdown}
    </div>
  );
};

export default MonitoringScheduleSelect;
